<template>

<div class="main list-view">

	<div class="sub-header">
		<div class="input-with-control">
			<input type="search" ref="search_term" v-model="filters.search_term" placeholder="Search..." @search="getList(1)" v-focus>
			<div class="button action input-go" @click="getList(1)"><i class="fas fa-search"></i></div>
		</div>
		<div class="controls">
			<pagination-detail v-if="showDetail" :list="list" :index="listIndex" @updatePage="getItem" />
			<pagination v-else :paging="paging" @updatePage="goToPage"></pagination>
		</div>
	</div>

	<div class="filters" v-if="showFilters">

		<div v-if="!showDetail">

			<div class="label-group">
				<label>Start</label>
				<input type="date" v-model="filters.start_date" :max="$today().clone().format('YYYY-MM-DD')">
			</div>
			<div class="label-group">
				<label>End</label>
				<input type="date" v-model="filters.end_date" :max="$today().clone().format('YYYY-MM-DD')">
			</div>
			<div class="label-group">
				<label></label>
				<div class="button small neutral" @click="getList(1)">Filter Dates</div>
			</div>

			<label>Order Type</label>
			<select v-model="filters.order_method_id" @change="toggleFilter('order_method')">
				<option value="">Select...</option>
				<option v-for="(orderMethod, index) in orderMethodCodes" :value="orderMethod.order_method_id">{{ orderMethod.description }}</option>
			</select>

			<label>Order Status</label>
			<select v-model="filters.order_status_id" @change="toggleFilter('order_status')">
				<option value="">Select...</option>
				<option v-for="(orderStatus, index) in orderStatusCodes" :value="orderStatus.order_status_id">{{ orderStatus.order_status_code }}</option>
			</select>

			<label>Payment Method</label>
			<select v-model="filters.payment_method_id" @change="toggleFilter('payment_method')">
				<option value="">Select...</option>
				<option v-for="(paymentMethod, index) in paymentMethodCodes" :value="paymentMethod.payment_method_id">{{ paymentMethod.description }}</option>
			</select>

			<template v-if="orderMethodCode != 'WEB'">
				<label>Order Source</label>
				<select v-model="filters.source_id" @change="toggleFilter('source_id')">
					<option value="">Select...</option>
					<option v-for="(orderSource, index) in orderSources" :value="orderSource.source_id">{{ orderSource.description }}</option>
				</select>
			</template>

			<label>Order Includes...</label>
			<ul class="checkbox-group" style="margin: 0 0 0.5em 0.5em;">
				<li>
					<span :class="['pointer', 'far', filters.has_long_guns ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('has_long_guns')"></span>
					<label>Long Guns</label>
				</li>
				<li>
					<span :class="['pointer', 'far', filters.has_handguns ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('has_handguns')"></span>
					<label>Handguns</label>
				</li>
				<li>
					<span :class="['pointer', 'far', filters.has_ammo ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('has_ammo')"></span>
					<label>Ammo</label>
				</li>
				<li>
					<span :class="['pointer', 'far', filters.has_discounts ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('has_discounts')"></span>
					<label>Discounts</label>
				</li>
				<li>
					<span :class="['pointer', 'far', filters.has_fees ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('has_fees')"></span>
					<label>Fees</label>
				</li>
				<li v-if="filters.order_method_id == 5">
					<span :class="['pointer', 'far', filters.include_zero_balance ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('include_zero_balance')"></span>
					<label>Zero Balance</label>
				</li>
			</ul>

			<div :class="['button', hasFilters ? 'neutral' : 'disabled']" @click="clearFilters(true)">Clear Filters</div>
			<div v-if="!showDetail" class="divider"></div>
			<div v-if="!showDetail" :class="['extra-menu-item', { 'disabled': gettingExport }]" @click="doExport()">Export List</div>
		</div>

		<div v-if="showDetail">
			<button class="button neutral" @click="backToList()">Back To List</button>
			<div class="divider"></div>
			<div v-if="orderDetail.order.order_method_code != 'ASSEMBLY'" :class="['extra-menu-item', { 'disabled': !orderDetail.billing.email_address || sendingEmails }]" @click="sendOrderEmails()">{{ orderDetail.order.confirmation_sent ? 'Res' : 'S' }}end Emails <i v-if="sendingEmails" class="fa fa-spinner fa-spin" /></div>
			<div class="extra-menu-item" @click="printOrder('dealer')">Print Order</div>
			<div class="extra-menu-item" @click="printOrder('customer')">Print Customer Copy</div>
			<div v-if="orderDetail.order.order_method_code != 'ASSEMBLY'" class="extra-menu-item" @click="printPackingSlip()">Print Packing Slip</div>
			<div v-if="orderDetail.order.order_method_code == 'ASSEMBLY'" class="extra-menu-item" @click="receiveItems()">Receive Items</div>
			<div v-if="getDevEnvironment && orderDetail.order.order_method_code != 'ASSEMBLY' && orderDetail.totals.order_total > 0" :class="['extra-menu-item', { 'disabled': orderDetail.order.order_status_code == 'RETURNED' }]" @click="confirmCancelOrder()">Cancel {{ $caplitalizeWords(orderDetail.order.order_method_code) }} Order (Beta)</div>
			<div v-if="orderDetail.order.order_method_code != 'ASSEMBLY'" :class="['extra-menu-item', { 'disabled': orderDetail.order.order_status_code == 'RETURNED' }]" @click="confirmReturnSale()">Record Return Sale</div>
		</div>
		<div v-if="previousRoute">
			<div class="divider"></div>
			<router-link class="extra-menu-item" :to="previousRoute">Back to {{ previousRoute.split('/')[1].capitalizeWords() }}</router-link>
		</div>
	</div>

	<div v-if="!showDetail" class="list">
	    <div v-if="list.length == 0" class="empty-list">
	        This list is empty
	    </div>
	    <table v-else class="data-list" cellpadding="0" cellspacing="0">
			<thead>
				<tr>
					<th class="list">Number</th>
					<th class="list">Date / Time</th>
					<th class="list" v-if="!orderMethodCode || filters.order_method_code.indexOf(',') > -1">Type / Status</th>
					<th class="list">{{ orderMethodCode == 'ASSEMBLY' ? 'Distributor' : 'Customer' }}</th>
					<th class="list right" v-if="orderMethodCode && orderMethodCode != 'ASSEMBLY'">Discount</th>
					<th class="list right" v-if="orderMethodCode && orderMethodCode != 'ASSEMBLY'">Fee</th>
					<th class="list right">Total</th>
					<th class="list right" v-if="orderMethodCode == 'LAYAWAY' || filters.has_balance">Balance</th>
				</tr>
			</thead>
			<tr v-for="(order, index) in list" class="clickable-list-item" @click="getItem(order.order_id)">
				<td class="list">{{ order.order_number }}</td>
				<td class="list">{{ order.order_date }}<br>{{ order.order_time }}</td>
				<td class="list" v-if="!orderMethodCode || filters.order_method_code.indexOf(',') > -1">{{ order.order_method }}<br>{{ order.order_status }}</td>
				<td class="list">
					{{ order.full_name }}
					<div v-if="order.address_1">
						{{ order.address_1 }} {{ order.city }} {{ order.state }} {{ order.zip_code }}
					</div>
				</td>
				<td class="list right" v-if="orderMethodCode && orderMethodCode != 'ASSEMBLY'">{{ order.discount_amount }}</td>
				<td class="list right" v-if="orderMethodCode && orderMethodCode != 'ASSEMBLY'">{{ order.fees_amount }}</td>
				<td class="list right">{{ order.order_total }}</td>
				<td class="list right" v-if="orderMethodCode == 'LAYAWAY' || filters.has_balance">{{ order.balance_remaining }}</td>
			</tr>
		</table>
	</div>

	<div v-if="showDetail" class="detail">
		<div v-if="loading" class="loading empty-list"><i class="fa fa-spinner fa-spin" /></div>
		<order-detail v-else :orderDetail="orderDetail" :orderStatusCodes="orderStatusCodes" :orderSources="orderSources" :packingSlip="showPackingSlip" :showPrint="showPrint" :key="'packing_slip_' + showPackingSlip + '_' + showPrint" @reload="getItem(orderDetail.order.order_id)" />
	</div>

	<div class="modal" v-if="showReturnSaleModal">
		<div class="inner">
			<h2>Confirm Record Return Sale</h2>
			<div class="content">
				<p>Record Return Sale is most commonly used to cancel a layaway order.</p>
				<p>It will return items to inventory, create a negative sale in item departments, and offset tax and fees in the current reporting period.</p>
				<p>It will not refund payments. Use POS to return items and refund any eligible payments to your customer.</p>
				<p>This action cannot be undone. Do you want to record a return sale?</p>
				<div v-if="modalMessage" class="empty">{{ modalMessage }}</div>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showReturnSaleModal = false">Cancel</div>
				<div class="button action spaced" @click="recordReturnSale()">Yes, Record Return Sale</div>
			</div>
		</div>
	</div>

	<div class="modal large-modal" v-if="showCancelOrderModal">
		<div class="inner">
			<h2>Confirm Cancel {{ orderDetail.order.order_method_code }} Order</h2>
			<div class="content">
				<div class="one-two-columns">
					<div>
						<p>Cancelling this order will return items to inventory and create a return sale to offset any tax and fees in the current reporting period.</p>
						<div v-if="cancelOrderDetail.refund_amount > 0" style="border-top: 1px solid #ccc; border-bottom: 1px solid #ccc; padding: 0.5em 0; margin: 0 0 1em 0;">
							<p>Is there a restock fee for cancelling this order?<br>Enter percent OR amount...</p>
							<div class="three-columns">
								<div>
									<label>Percent</label>
									<div><number-input v-model="restockFee.percent" style="display: inline-block;" :key="'rf_' + restockFee.percent" /></div>
								</div>
								<div>
									<label>Amount</label>
									<div><number-input v-model="restockFee.amount" style="display: inline-block;" :key="'rf_' + restockFee.amount" /></div>
								</div>
								<div>
									<div class="button smaller full-width neutral" style="margin: 0.5em 0;" @click="applyRestockFee()">{{ restockFee.amount ? 'Change' : 'Apply' }}</div>
									<div :class="['button', 'smaller', 'full-width', restockFee.amount ? 'neutral' : 'disabled' ]" @click="removeRestockFee()">Remove</div>
								</div>
							</div>
						</div>
						<p v-if="cancelOrderDetail.payments.AUTH_CODE">
							NOTE: Orders paid with <b>Auth Code</b> require that you manually log in to your merchant account dashboard to manage the refund.
						</p>
						<p v-if="cancelOrderDetail.payments.FINANCING">
							NOTE: Orders with <b>Third Party Financing</b> require that you manually log in to your financing company's dashboard to manage the contract.
						</p>
						<p style="margin: 2em 1em; font-size: 1.25em;">Cancelling an order cannot be undone!<br><br>Please review all details before proceeding.</p>
					</div>
					<div style="border-left: 1px solid #ccc; padding: 0 0 0 1em;">

						<div v-if="cancelOrderDetail.refund_amount > 0">
							<h3>Payments will be refunded</h3>
							<div class="indent">
								<table class="list item-list" style="width: 100%;">
									<thead>
										<tr>
											<th>Type</th>
											<th class="right">Amount</th>
											<th class="right">Restock</th>
											<th class="right">Refund</th>
										</tr>
									</thead>
									<tr v-for="(payment, index) in cancelOrderDetail.payments" class="total-line">
										<td class="list">
											{{ $caplitalizeWords(payment.payment_method_code) }}
											<span v-if="payment.reference_number">{{ payment.reference_number }}</span>
											<!-- <span v-if="['CREDIT_CARD', 'CREDIT_CARD_MANUAL', 'DEBIT_CARD', 'GIFT_CARD'].includes(payment.payment_method_code)" class="note">(will be automatically refunded)</span>
											<span v-else class="note error">(requires manual refund to customer)</span> -->
										</td>
										<td class="list right">{{ $formatCurrency(payment.original_amount) }}</td>
										<td class="list right">{{ $formatCurrency(payment.restock_applied) }}</td>
										<td class="list right">{{ $formatCurrency(payment.amount) }}</td>
									</tr>
								</table>
							</div>
							<h3 style="border-top: 1px solid #ccc; padding: 0.25em 0 0 0;">Or refund to a gift card</h3>
							<div class="indent three-columns" style="display: grid; grid-template-columns: 1fr 10em 10em;">
								<div>
									<label>Gift Card Number</label>
									<input type="text" v-model="cancelOrderDetail.new_gift_card_number" class="inline" />
								</div>
								<div>
									<label>Amount</label>
									<input type="text" v-model="cancelOrderDetail.refund_amount" readonly />
								</div>
								<div><div class="button neutral small">Apply</div></div>
							</div>
						</div>

						<h3 style="border-top: 1px solid #ccc; padding: 0.25em 0 0 0;">Items will be restocked</h3>
						<div class="indent">
							<table class="list item-list" style="width: 100%;">
								<thead>
									<tr>
										<th class="list"></th>
										<th class="list">Item</th>
										<th class="list right">Price</th>
										<th class="list right">Qty</th>
										<th class="list right">Ext</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="(item, index) in orderDetail.items" class="item-line" :key="'item_' + index">
										<td class="list center"><div style="width: 3em; height: 3em;"><img :src="item.image_src" style="max-width: 100%; max-height: 100%;"></div></td>
										<td class="list">
											<label>{{ item.upc ? 'UPC' : 'ID' }}</label> {{ item.upc || item.product_id }}
											<div class="item-description">{{ item.description }}</div>
											<div v-if="item.discount_amount"><label>Discount</label>${{ item.discount_amount }}, {{ item.override_reason }}</div>
											<div v-if="item.serial_number" style="padding: 0 0 0 1em;"><label>SN</label>{{ item.serial_number }}</div>
											<div v-if="item.notes"><label>Note</label>{{ item.notes }}</div>
										</td>
										<td class="list right">${{ item.order_price }}</td>
										<td class="list right">{{ item.quantity }}</td>
										<td class="list right">${{ (item.quantity * item.order_price).toFixed(2) }}</td>
									</tr>
								</tbody>
							</table>
						</div>

						<h3 style="border-top: 1px solid #ccc; padding: 0.25em 0 0 0;">Fees and taxes will be credited</h3>
						<div class="indent">
							<table class="list item-list" style="width: 100%;">
								<tr class="total-line" v-for="(discount, index) in orderDetail.discounts">
									<td class="list right"><label>{{ discount.description }}</label></td>
									<td class="list right" style="width: 8em;">-${{ discount.amount }}</td>
								</tr>
								<tr class="total-line" v-for="(fee, index) in orderDetail.fees">
									<td class="list right"><label>{{ fee.description }}</label></td>
									<td class="list right" style="width: 8em;">${{ fee.amount }}</td>
								</tr>
								<tr class="total-line">
									<td class="list right"><label>Shipping</label></td>
									<td class="list right" style="width: 8em;">${{ orderDetail.totals.shipping_charge }}</td>
								</tr>
								<tr class="total-line">
									<td class="list right"><label>Handling</label></td>
									<td class="list right" style="width: 8em;">${{ orderDetail.totals.handling_charge }}</td>
								</tr>
								<tr class="total-line">
									<td class="list right"><label>Sales Tax</label></td>
									<td class="list right" style="width: 8em;">${{ orderDetail.totals.tax_charge }}</td>
								</tr>
							</table>
						</div>

						<h3 style="border-top: 1px solid #ccc; padding: 0.25em 0 0 0;">
							<div style="float: right; margin: 0 0.5em 0 0;">{{ $formatCurrency(cancelOrderDetail.refund_amount) }}</div>
							Total refund due to customer
						</h3>

					</div>
				</div>

				<div v-if="modalMessage" class="message-overlay">
					<div class="empty" v-html="modalMessage"></div>
					<div v-if="saving" class="empty"><i class="fa fa-spinner fa-spin" /></div>
					<div v-else class="empty"><i :class="['fa', modalSuccess ? 'fa-check' : 'fa-exclamation-triangle']" /></div>
				</div>

			</div>
			<div :class="['controls', { disabled: saving } ]">
				<div :class="['button', 'spaced', saving ? 'disabled' : 'cancel']" @click="showCancelOrderModal = (saving ? true : false)">Close</div>
				<div :class="['button', saving || !modalSuccess ? 'disabled' : 'action', 'spaced']" @click="recordCanelOrder()">Cancel This {{ orderDetail.order.order_method_code }} Order</div>
			</div>
		</div>
	</div>

	<message-modal v-if="message" :message="message" :loading="loading" :errorStatus="messageStatus" :timeout="messageTimeout" @dismiss="message = ''" />

	<iframe id="download" src="" style="display: none;"></iframe>

	<help-popup :helpLink="{348:'In-Store Sales', 351:'Online Sales', 356:'Distributor Orders'}" :tours="false"></help-popup>

</div>

</template>

<script>
import OrderDetail from '@/components/OrderDetail.vue'
import NumberInput from '@/components/NumberInput.vue'
import HelpPopup from '@/components/HelpPopup.vue'

export default {
	name: 'order-list',
	props: ['order_id'],
    components: {
        'order-detail': OrderDetail,
		'number-input': NumberInput,
		'help-popup': HelpPopup
	},
	data() {
		return {
            loading: false,
			message: '',
			messageStatus: '',
			messageTimeout: 0,
			orderMethodCodes: [],
			orderMethodCode: '',
			orderStatusCodes: [],
			paymentMethodCodes: [],
			orderSources: [],
			showFilters: true,
			filters: {},
            default_start_date: '',
            default_end_date: '',
			filterMessage: '',
			filterLoading: false,
			list: [],
			listIndex: 0,
			paging: {},
			showDetail: false,
			orderDetail: {},
			exportMessage: '',
			gettingExport: false,
			orderStatus: '',
			showPackingSlip: false,
			showPrint: false,
			showReturnSaleModal: false,
			modalMessage: '',
			modalSuccess: false,
			saving: false,
			sendingEmails: false,
			showCancelOrderModal: false,
			cancelOrderDetail: {},
			restockFee: { percent: '', amount: '', description: '' },
			previousRoute: ''
		}
	},
	created() {
		if (sessionStorage.getItem('returnRoute')) {
			this.previousRoute = sessionStorage.getItem('returnRoute')
			sessionStorage.removeItem('returnRoute')
		}
		if (!this.order_id) {
			this.fetchRouteLastRequested()
		}
		var oneMonthAgo = new Date()
		oneMonthAgo.setMonth(oneMonthAgo.getMonth() -1)
		this.default_start_date = oneMonthAgo.toISOString().slice(0,10)
		this.default_end_date = new Date().toISOString().slice(0,10)
        this.updateCustomRouteName()
		this.getOrderMethods()
		if (this.order_id) {
			this.getItem(this.order_id)
		}
	},
	computed: {
		hasFilters() {
			for (const [key, value] of Object.entries(this.filters)) {
				if (['page', 'fd_token', 'source', 'hostname', 'app_route'].includes(key)) { continue }
				if (key == 'start_date' && value == this.default_start_date) { continue }
				if (key == 'end_date' && value == this.default_end_date) { continue }
				if (value) {
					return true
				}
			}
			return false
		}
	},
	watch: {
        '$route.path' (newPath, oldPath) {
            if (newPath != oldPath) {
				let routeSection = this.$route.path.split('/').pop()
				this.orderMethodCodes.forEach(element => {
					if (element.order_method_code.toLowerCase() == routeSection) {
						this.filters.order_method_id = element.order_method_id
						this.orderMethodCode = element.order_method_code
					}
				})
                this.getList()
			}
		},
        '$route.query' (newQuery, oldQuery) {
            if (newQuery != oldQuery) {
			// if source in query, update filter and reset route
				if (this.$route.query.source) {
					this.orderMethodCodes.forEach(element => {
						if (element.order_method_code.toLowerCase() == this.$route.query.source) {
							this.filters.order_method_id = element.order_method_id
							this.orderMethodCode = element.order_method_code
						}
					})
					this.updateCustomRouteName(this.orderMethodCode);
				}
                this.getList()
            }
        }
	},
	beforeRouteLeave(to, from, next) {
		// store filters in session storage
		sessionStorage.setItem('order_list_filters', JSON.stringify(this.filters))
		next()
	},
	methods: {
		getOrderMethods() {
			this.apiCall({ destination: 'get_list_order_control_lists' }).then(obj => {
				this.orderMethodCodes = obj.order_methods
				this.orderStatusCodes = obj.order_status
				this.paymentMethodCodes = obj.payment_methods
				this.orderSources = obj.order_sources
				this.clearFilters(true)
			}).catch(err => {
				console.log(err)
			})
		},
		clearFilters(getList) {
			// if there are filters is session storgage?
			var sessionFilters = sessionStorage.getItem('order_list_filters')
			if (sessionFilters) {
				this.filters = JSON.parse(sessionFilters)
				sessionStorage.removeItem('order_list_filters')
			} else {
				this.filters = {
					page: 1,
					search_term: '',
					contact_id: '',
					order_method_id: '',
					order_method_code: '',
					order_status_id: '',
					payment_method_id: '',
					source_id: '',
					has_discounts: '',
					has_fees: '',
					has_balance: '',
					include_zero_balance: '',
					start_date: '',
					end_date: '',
					do_export: false
				}
				this.filters.start_date = this.default_start_date
				this.filters.end_date = this.default_end_date
			}
			if (this.$route.query.source) {
				// if source in query, update filter and reset route
				this.orderSources.forEach(element => {
					if (element.source_code.toLowerCase() == this.$route.query.source) {
						this.filters.source_id = element.source_id
					}
				})
				this.updateCustomRouteName(this.orderMethodCode);
				this.$router.push('/orders-sales/orders')
			} else if (this.$route.query.contact_id) {
				this.filters.contact_id = this.$route.query.contact_id
				this.filters.start_date = ''
				this.filters.end_date = ''
			} else {
				let routeSection = this.$route.path.split('/').pop()
				this.orderMethodCodes.forEach(element => {
					if (element.order_method_code.toLowerCase() == routeSection) {
						this.filters.order_method_id = element.order_method_id
						this.orderMethodCode = element.order_method_code
					}
				})
			}
			if (getList && !this.order_id) {
				this.getList(1)
			}
		},
		toggleFilter(which) {
			switch (which) {
				case "order_method":
					this.orderMethodCode = ''
					this.orderMethodCodes.forEach(element => {
						if (element.order_method_id == this.filters.order_method_id) {
							this.orderMethodCode = element.order_method_code
						}
					})
					this.$router.push({ path: '/orders-sales/orders/' + this.orderMethodCode.toLowerCase() })
					break;
				case "order_status":
				case "payment_method":
				case "source_id":
					break;
				default:
					this.filters[which] = !this.filters[which]
					break;
			}
			this.filters.page = 1
			this.getList()
		},
		getList(page) {
			if (this.loading) { return }
			if (page) {
				this.filters.page = page
			}
			this.message = 'Loading orders...'
			this.messageStatus = 'waiting'
			this.showDetail = false
			this.orderDetail = {}
			this.loading = true
			this.apiCall({ destination: 'get_list_orders_expanded', data: this.filters }).then(obj => {
				this.loading = false
				this.messageStatus = obj.status
				this.message = obj.message
				if (obj.status == 'success') {
					if (this.filters.do_export) {
						this.filters.do_export = 0
							setTimeout(() => {
								if (obj.status == 'success') {
									document.getElementById("download").src = obj.download_url
								}
								this.gettingExport = false
								this.message = ''
							}, 1000)
					} else {
						this.message = ''
						this.list = obj.list
						this.paging = obj.paging
					}
				}
			}).catch(err => {
				console.log(err)
			})
		},
		goToPage(page) {
			this.filters.page = page
			this.getList()
		},
		getItem(order) {
			let orderId = typeof order === 'object' ? order.order_id : order
			this.loading = true
			for (let i = 0; i < this.list.length; i++) {
				if (this.list[i].order_id == orderId) {
					this.listIndex = i
					break
				}
			}
			this.apiCall({ destination: 'get_item_order', data: { order_id: orderId } }).then(obj => {
				this.orderDetail.order = obj.order_detail
				this.orderDetail.billing = obj.order_billing_address
				this.orderDetail.custom_fields = obj.order_billing_custom_fields
				this.orderDetail.shipping = obj.order_shipping_address
				this.orderDetail.items = obj.order_items
				this.orderDetail.item_fees = obj.order_item_fees
				this.orderDetail.discounts = obj.order_discounts
				this.orderDetail.fees = obj.order_fees
				this.orderDetail.payments = obj.order_payments
				this.orderDetail.totals = obj.order_totals
				this.orderStatus = obj.order_detail.order_status_code
				this.updateCustomRouteName('Detail');
				this.loading = false
				this.showDetail = true
				this.cancelOrderDetail = {
					order_id: orderId,
					restock_fee: '',
					refund_amount: 0,
					payments: {}
				}
				this.restockFee = { percent: '', amount: '', description: '' }
				this.saving = false
			}).catch(err => {
				console.log(err)
			})
		},
		// printOrder() {
		// 	window.print()
		// },
		printOrder(which) {
			this.orderDetail.exclude_cost = (which == 'customer')
			this.showPrint = true
			setTimeout(() => {
				window.print()
				this.showPrint = false
			}, 500)
		},
		printPackingSlip() {
			this.showPackingSlip = true
			// slight delay while the screen renders the packing slip!
			setTimeout(() => {
				window.print()
				this.showPackingSlip = false
			}, 500)
		},

		applyRestockFee() {
			if (this.saving) { return }
			if (this.restockFee.amount == '' && this.restockFee.percent == '') { return }
			let useTotal = Number(this.orderDetail.totals.payment_total)
			let feeDescription = 'Restock Fee'
			if (this.restockFee.percent) {
				if (Number(this.restockFee.percent) < 1) {
					this.restockFee.percent = Number(this.restockFee.percent) * 100
				}
				feeDescription += ' (' + this.restockFee.percent + '%)'
				let percent = Number(this.restockFee.percent) / 100
				// use Math.abs on total so fees can be applied to refunds
				this.restockFee.amount = Number(useTotal * percent).toFixed(2)
				this.restockFee.percent = ''
			} else {
				this.restockFee.amount = Number(this.restockFee.amount).toFixed(2)
			}
			// don't allow restock fee greater than total refund due!
			if (parseFloat(this.restockFee.amount) > parseFloat(useTotal)) {
				this.restockFee.amount = ''
				return
			}
			this.restockFee.description = feeDescription
			//this.restockFeeApplied = true
			this.confirmCancelOrder()
		},
		removeRestockFee() {
			if (this.saving) { return }
			if (!this.restockFee.percent && !this.restockFee.amount) { return }
			this.restockFee = { percent: '', amount: '', description: '' }
			//this.restockFeeApplied = false
			this.confirmCancelOrder()
		},

		confirmCancelOrder() {
			if (this.saving) { return }
			if (this.orderMethodCode && this.orderMethodCode == 'ASSEMBLY') { return }
			if (this.orderDetail.order.order_status_code == 'RETURNED') { return }
			// if modal is live, and new restock fee is greater than total refund amount
			if (this.showCancelOrderModal && this.restockFee.amount > (this.cancelOrderDetail.refund_amount + parseFloat(this.cancelOrderDetail.restock_fee))) {
				this.modalMessage = 'Restock fee cannot be more than total refund due!'
				setTimeout(() => {
					this.restockFee.amount = this.cancelOrderDetail.restock_fee
					this.modalMessage = ''
				}, 2000)
				return
			}
			this.saving = true
			this.modalMessage = 'Checking order cancellation eligibility...'
			this.modalSuccess = false
			this.showCancelOrderModal = true
			this.cancelOrderDetail.order_id = this.orderDetail.order.order_id
			this.cancelOrderDetail.restock_fee = this.restockFee.amount
			this.apiCall({ destination: 'get_order_cancel_eligibility', data: this.cancelOrderDetail }).then(obj => {
				this.saving = false
				if (obj.status == 'error') {
					this.modalMessage = obj.message
				} else if (obj.pos_only) {
					this.modalMessage = 'This order contains a credit card terminal transaction<br>which can only be refunded through POS.'
				} else {
					this.modalSuccess = true
					this.cancelOrderDetail.payments = obj.payments
					this.cancelOrderDetail.refund_amount = obj.total_refund_due
					this.modalMessage = ''
				}
			})
		},
		recordCanelOrder() {
			if (this.saving || !this.modalSuccess) { return }
			this.modalMessage = 'Doing the thing!'
			this.saving = true
			this.apiCall({ destination: 'save_order_cancel', data: this.cancelOrderDetail }).then(obj => {
				this.saving = false
				if (obj.status == 'error') {
					this.modalMessage = obj.message
				} else {
					this.modalSuccess = true
					this.modalMessage = 'Done!'
				}
			})
		},

		confirmReturnSale() {
			if (this.orderMethodCode && this.orderMethodCode == 'ASSEMBLY') { return }
			if (this.orderDetail.order.order_status_code == 'RETURNED') { return }
			this.showReturnSaleModal = true
		},
		recordReturnSale() {
			if (this.orderMethodCode && this.orderMethodCode == 'ASSEMBLY') { return }
			if (this.orderDetail.order.order_status_code == 'RETURNED') { return }
			if (this.saving) { return }
			this.saving = true
			this.modalMessage = 'Recording return sale...'
			this.apiCall({ destination: 'save_item_return_sale', data: { order_id: this.orderDetail.order.order_id } }).then(obj => {
				this.saving = false
				this.modalMessage = obj.message
				if (obj.status == 'success') {
					setTimeout(() => {
						this.showReturnSaleModal = false
						this.getItem(this.orderDetail.order.order_id)
					}, 2000)
				}
			}).catch(err => {
				console.log(err)
			})
		},
		sendOrderEmails() {
			if (this.sendingEmails) { return }
			if (!this.orderDetail.billing.email_address) { return }
			this.sendingEmails = true
			this.apiCall({ destination: 'send_confirmation_email', data: { order_id: this.orderDetail.order.order_id } }).then(obj => {
				this.sendingEmails = false
				this.modalMessage = obj.message
				if (obj.status == 'success') {
					setTimeout(() => { this.showReturnSaleModal = false }, 2000)
				}
			}).catch(err => {
				console.log(err)
			})
		},
		backToList() {
			this.updateCustomRouteName(this.orderMethodCode);
			this.showDetail = false
			this.orderDetail = {}
			this.getList()
		},
		doExport() {
			if (this.gettingExport) { return false }
			this.gettingExport = true
			this.exportMessage = 'Generating CSV for ' + this.paging.total_items + ' items...'
			this.filters.order_id = ''
			this.filters.do_export = true
			this.getList(1)
		},
		receiveItems() {
			let distributorId = Object.values(this.orderDetail.items)[0].fulfilled_distributor_id
			this.apiCall({ destination: 'get_distributor_detail', data: { distributor_id: distributorId } }).then(obj => {
				let supplierDetail = obj.distributor
				supplierDetail.full_name = supplierDetail.company_name
				supplierDetail.order_number = this.orderDetail.order.order_number
				supplierDetail.supplier_id = distributorId
				sessionStorage.setItem('supplier_detail', JSON.stringify(supplierDetail))
				this.$router.push('/inventory/receive-inventory/new')
			}).catch(err => {
				console.log(err)
			})
		}
	}
}
</script>

<style lang="scss" scoped>
</style>
